/**
 * SearchReplacePanel Component
 * Compact horizontal bar for search & replace within the editor.
 */

import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronUp,
  ChevronDown,
  X,
  CaseSensitive,
  Replace,
  ReplaceAll,
} from 'lucide-react';
import type { useSearchReplace } from './useSearchReplace';

type SearchReplaceHook = ReturnType<typeof useSearchReplace>;

interface SearchReplacePanelProps {
  hook: SearchReplaceHook;
  onClose: () => void;
}

export const SearchReplacePanel: React.FC<SearchReplacePanelProps> = ({
  hook,
  onClose,
}) => {
  const { t } = useTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus search input on open
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) {
        hook.prevMatch();
      } else {
        hook.nextMatch();
      }
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      hook.clearSearch();
      onClose();
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
      {/* Search input */}
      <div className="flex items-center gap-1">
        <input
          ref={searchInputRef}
          type="text"
          value={hook.searchTerm}
          onChange={(e) => hook.setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('documentEditor.searchReplace.searchPlaceholder')}
          className="w-40 sm:w-52 px-2.5 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <button
          onClick={hook.toggleCaseSensitive}
          title={t('documentEditor.searchReplace.caseSensitive')}
          className={`p-1.5 rounded transition-colors ${
            hook.caseSensitive
              ? 'bg-indigo-100 text-indigo-700'
              : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'
          }`}
        >
          <CaseSensitive className="w-4 h-4" />
        </button>
      </div>

      {/* Match counter + navigation */}
      <div className="flex items-center gap-1">
        <span className="text-xs text-slate-500 min-w-[4rem] text-center">
          {hook.matchCount > 0
            ? t('documentEditor.searchReplace.matchCount', {
                current: hook.currentMatch + 1,
                total: hook.matchCount,
              })
            : hook.searchTerm.length >= 2
              ? t('documentEditor.searchReplace.noMatches')
              : ''}
        </span>
        <button
          onClick={hook.prevMatch}
          disabled={hook.matchCount === 0}
          title="Previous (Shift+Enter)"
          className="p-1 rounded text-slate-500 hover:text-slate-700 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
        <button
          onClick={hook.nextMatch}
          disabled={hook.matchCount === 0}
          title="Next (Enter)"
          className="p-1 rounded text-slate-500 hover:text-slate-700 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>

      {/* Replace input */}
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={hook.replaceTerm}
          onChange={(e) => hook.setReplaceTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('documentEditor.searchReplace.replacePlaceholder')}
          className="w-40 sm:w-52 px-2.5 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <button
          onClick={hook.replaceCurrent}
          disabled={hook.matchCount === 0}
          title={t('documentEditor.searchReplace.replace')}
          className="p-1.5 rounded text-slate-500 hover:text-slate-700 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <Replace className="w-4 h-4" />
        </button>
        <button
          onClick={hook.replaceAll}
          disabled={hook.matchCount === 0}
          title={t('documentEditor.searchReplace.replaceAll')}
          className="p-1.5 rounded text-slate-500 hover:text-slate-700 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ReplaceAll className="w-4 h-4" />
        </button>
      </div>

      {/* Close */}
      <button
        onClick={() => {
          hook.clearSearch();
          onClose();
        }}
        title={t('documentEditor.searchReplace.close')}
        className="p-1.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors ml-auto"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default SearchReplacePanel;
